<template>
  <div class="flex flex-col dmcenter">
    <div
      class="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-between sm:items-center p-4 sm:p-5 bg-white border border-gray-100 rounded-t-lg shadow-sm"
    >
      <h1 class="font-semibold text-base sm:text-lg text-gray-900">
        Total Retailers {{ total_retailers }} {{ country }}
      </h1>
      <div class="flex flex-wrap items-center gap-3">
        <button
          v-if="
            getUser.primaryaccount == 'junior_administrator' ||
            getUser.primaryaccount == 'super_administrator'
          "
          class="rounded-lg px-4 py-2 bg-express text-white text-sm font-medium flex items-center space-x-2 hover:bg-expressbutton transition-colors"
          @click="show_add_retailer = !show_add_retailer"
        >
          <i class="fas fa-plus"></i>
          <span>Add Retailer</span>
        </button>
        <button
          class="rounded-lg px-4 py-2 border border-gray-200 text-sm font-medium text-expressgray flex items-center space-x-2 hover:bg-gray-50 transition-colors"
          @click="download_csv"
        >
          <i class="fas fa-cloud-download-alt"></i>
          <span>Download</span>
        </button>
        <button
          @click="reload_list"
          class="rounded-lg px-4 py-2 border border-gray-200 text-sm font-medium text-expressgray flex items-center space-x-2 hover:bg-gray-50 transition-colors"
        >
          <i class="fas fa-sync"></i>
          <span>Refresh</span>
        </button>
      </div>
    </div>

    <div class="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white border border-gray-100 rounded-lg shadow-sm p-4 mb-4">
      <div class="flex w-full sm:w-auto space-x-2">
        <input
          class="w-full sm:w-80 border border-gray-200 rounded-lg pl-4 pr-9 py-2.5 text-sm focus:outline-none focus:border-express"
          type="text"
          v-model.trim="search"
          placeholder="Search Table"
        />
      </div>

      <div class="flex items-center space-x-3 text-sm text-expressgray flex-wrap gap-y-2">
        <p class="whitespace-nowrap">Records Per-Page</p>
        <select v-model="perPageChoice" @change="onPerPageChoiceChange" class="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-express">
          <option value="50">50</option>
          <option value="200">200</option>
          <option value="500">500</option>
          <option value="1000">1000</option>
          <option value="all">All</option>
          <option value="custom">Custom</option>
        </select>
        <input
          v-if="perPageChoice === 'custom'"
          type="number"
          min="1"
          v-model.number="customPerPage"
          @change="changePerPage"
          placeholder="Enter number"
          class="w-32 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-express"
        />
      </div>
    </div>

    <div class="flex w-full flex-col bg-white border-x border-b border-gray-100 rounded-b-lg shadow-sm mb-4">
      <div v-if="isLoading">
        <skeleton-table :rows="8" :columns="8" />
      </div>
      <div v-else-if="hasError" class="flex flex-col items-center justify-center space-y-3 p-10 text-center">
        <p class="text-sm text-expressgray">Unable to load retailers for {{ country }}. Please try again.</p>
        <button
          type="button"
          @click="reload_list"
          class="rounded-lg px-4 py-2 bg-express text-white text-sm font-medium hover:bg-expressbutton transition-colors"
        >
          Retry
        </button>
      </div>
      <div v-else>
        <div v-if="show_add_retailer">
          <add_retailer_model
            :staff_email="getUser.email"
            @completeTransaction="completeTransaction"
            @cancelTransaction="cancelTransaction"
          />
        </div>

        <div v-if="show_edit_retailer_model">
          <edit_retailer_model
            :selected_retailer="selected_retailer"
            @completeTransaction="completeEditRetailerTransaction"
            @cancelTransaction="cancelEditRetailerTransaction"
          />
        </div>

        <div v-if="show_recharge_model">
          <admin_retailer_add_wallet
            :business_name="retailer_form.business_name"
            :country="retailer_form.country"
            :mobile="retailer_form.mobile"
            :user_id="retailer_form.user_id"
            @completeRechargeTransaction="completeRechargeTransaction"
            @cancelRechargeTransaction="cancelRechargeTransaction"
          />
        </div>

        <div v-if="show_retailer_to_decommission">
          <add_retailer_to_decommission
            :name="selected_retailer_name"
            :phone="selected_retailer_mobile"
            :retailer_id="selected_retailer_id"
            @completeDecommissionTransaction="completeDecommissionTransaction"
            @cancelDecommissionTransaction="cancelDecommissionTransaction"
          />
        </div>

        <retailer-list-table
          :rowRaws="filteredRetailers"
          :account_type="getUser.primaryaccount"
          :currentPage="currentPage"
          :perPage="perPage"
          @rechargeRetailer="rechargeRetailer"
          @decommission_list_event="decommission_list_event"
          @retailer_edit_event="retailer_edit_event"
        />
      </div>
    </div>

    <div v-if="!isLoading && totalPages > 1" class="flex justify-center bg-white border border-gray-100 rounded-lg shadow-sm p-4 mb-4">
      <pagination
        :total-pages="totalPages"
        :total="total"
        :per-page="perPage"
        :current-page="currentPage"
        :has-more-pages="hasMorePages"
        @pagechanged="showMore"
      >
      </pagination>
    </div>
  </div>
</template>

<script>
import add_retailer_to_decommission from "@/components/uicomponents/models/add_retailer_to_decommission";
import { mapGetters } from "vuex";
import RetailerListTable from "@/components/uicomponents/tables/RetailerListTable";
import add_retailer_model from "@/components/uicomponents/models/add_retailer_model";
import admin_retailer_add_wallet from "@/components/uicomponents/models/admin_retailer_add_wallet";
import edit_retailer_model from "@/components/uicomponents/models/edit_retailer_model";
import SkeletonTable from "@/components/uicomponents/loader/SkeletonTable";

export default {
  name: "retailerstable",
  props: ["country"],
  async created() {
    this.isLoading = true;
    this.hasError = false;

    try {
      await this.$store.dispatch("admins/action_retailer_list", {
        page: this.currentPage,
        perPage: this.perPage,
        country: this.country,
        toast: this.$toast,
        router: this.$router,
      });

      this.filter_retailers();
    } catch (error) {
      this.hasError = true;
      this.isLoading = false;
    }
  },
  components: {
    // Add_retailer_to_decommission,
    add_retailer_to_decommission,
    RetailerListTable,
    add_retailer_model,
    admin_retailer_add_wallet,
    edit_retailer_model,
    SkeletonTable,
  },
  data() {
    return {
      show_add_retailer: false,
      show_recharge_model: false,
      show_retailer_to_decommission: false,
      show_edit_retailer_model: false,
      selected_retailer: {},
      selected_retailer_id: null,
      selected_retailer_mobile: null,
      selected_retailer_name: null,
      total_retailers: null,
      isLoading: false,
      hasError: false,
      totalPages: null,
      total: 100,
      perPage: 100000,
      perPageChoice: "all",
      customPerPage: null,
      currentPage: 1,
      hasMorePages: false,
      retailers: [],
      retailers_by_id: {},
      search: "",
      column: [
        "Full Name",
        "Email",
        "Mobile",
        "Business Name",
        "Address",
        "Country",
        "Wallet Balance",
        "Account Status",
      ],
      retailer_form: {
        country: null,
        user_id: null,
        business_name: null,
        mobile: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      retailer_list: "admins/retailer_list",
      retailer_list_ghana: "admins/retailer_list_ghana",
      retailer_list_nigeria: "admins/retailer_list_nigeria",
      retailer_list_civ: "admins/retailer_list_civ",
      getUser: "authentication/getUser",
    }),
    filteredRetailers() {
      const term = this.search.toLowerCase();
      if (!term) {
        return this.retailers;
      }
      return this.retailers.filter((row) => row.join(" ").toLowerCase().includes(term));
    },
  },
  methods: {
    completeDecommissionTransaction(data) {
      // console.log('retailerstable completeDecommissionTransaction', data)
      // retailer_id: payload.retailer_id,
      //     reason:  payload.reason,
      //     rde_personnel: payload.rde_personnel,
      //     status: payload.status
      this.$store.dispatch("admins/action_decommission_create", {
        retailer_id: data.retailer_id,
        reason: data.reason,
        rde_personnel: data.rde_personnel,
        status: data.status,
        toast: this.$toast,
        router: this.$router,
      });

      this.show_retailer_to_decommission = false;
    },
    cancelDecommissionTransaction() {
      this.show_retailer_to_decommission = false;
    },
    retailer_edit_event(data) {
      this.selected_retailer = this.retailers_by_id[data.user_id] || {};
      this.show_edit_retailer_model = true;
    },
    async completeEditRetailerTransaction(data) {
      this.show_edit_retailer_model = false;
      this.isLoading = true;
      this.hasError = false;

      await this.$store.dispatch("retailers/action_update_retailer_information", {
        data: data,
        toast: this.$toast,
        router: this.$router,
      });

      try {
        await this.$store.dispatch("admins/action_retailer_list", {
          country: this.country,
          page: this.currentPage,
          perPage: this.perPage,
          toast: this.$toast,
          router: this.$router,
        });

        this.filter_retailers();
      } catch (error) {
        this.hasError = true;
        this.isLoading = false;
      }
    },
    cancelEditRetailerTransaction() {
      this.show_edit_retailer_model = false;
    },
    decommission_list_event(data) {
      this.selected_retailer_id = data.retailer_id;
      this.selected_retailer_mobile = data.mobile;
      this.selected_retailer_name = data.name;
      this.show_retailer_to_decommission = true;
    },
    rechargeRetailer(data) {
      this.retailer_form.country = data.country;
      this.retailer_form.business_name = data.business_name;
      this.retailer_form.mobile = data.mobile;
      this.retailer_form.user_id = data.user_id;

      this.show_recharge_model = true;
    },
    completeRechargeTransaction(data) {
      this.retailer_form.country = null;
      this.retailer_form.business_name = null;
      this.retailer_form.mobile = null;
      this.retailer_form.user_id = null;
      this.show_recharge_model = false;
    },
    cancelRechargeTransaction() {
      this.retailer_form.country = null;
      this.retailer_form.business_name = null;
      this.retailer_form.mobile = null;
      this.retailer_form.user_id = null;
      this.show_recharge_model = false;
    },
    completeTransaction(data) {
      this.$store.dispatch("retailers/action_retailer_create_account", {
        form: data,
        toast: this.$toast,
        router: this.$router,
      });

      this.show_add_retailer = false;
    },
    cancelTransaction() {
      this.show_add_retailer = false;
    },

    download_csv() {
      var csv = "";
      this.retailers.forEach(function (row) {
        csv += row.join(",");
        csv += "\n";
      });
      var hiddenElement = document.createElement("a");
      hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
      hiddenElement.target = "_blank";
      hiddenElement.download = "retailers.csv";
      hiddenElement.click();
    },
    async showMore(data) {
      this.currentPage = data;
      this.isLoading = true;
      this.hasError = false;

      try {
        await this.$store.dispatch("admins/action_retailer_list", {
          country: this.country,
          page: this.currentPage,
          perPage: this.perPage,
          toast: this.$toast,
          router: this.$router,
        });

        this.filter_retailers();
      } catch (error) {
        this.hasError = true;
        this.isLoading = false;
      }
    },
    onPerPageChoiceChange() {
      if (this.perPageChoice === "custom") {
        return;
      }
      this.perPage =
        this.perPageChoice === "all"
          ? this.total || 100000
          : Number(this.perPageChoice);
      this.changePerPage();
    },
    async changePerPage() {
      if (this.perPageChoice === "custom") {
        if (!this.customPerPage || this.customPerPage < 1) {
          return;
        }
        this.perPage = this.customPerPage;
      }

      this.currentPage = 1;
      this.isLoading = true;
      this.hasError = false;

      try {
        await this.$store.dispatch("admins/action_retailer_list", {
          country: this.country,
          page: this.currentPage,
          perPage: this.perPage,
          toast: this.$toast,
          router: this.$router,
        });

        this.filter_retailers();
      } catch (error) {
        this.hasError = true;
        this.isLoading = false;
      }
    },
    currentCountryList() {
      if (this.country === "Ghana") {
        return this.retailer_list_ghana;
      }
      if (this.country === "CIV") {
        return this.retailer_list_civ;
      }
      return this.retailer_list_nigeria;
    },
    filter_retailers() {
      let myresult = [];
      let myretailers_by_id = {};
      const myretailers = this.currentCountryList();
      this.total_retailers = myretailers.total;

      this.hasMorePages = !(
        typeof myretailers.next_page_url === "undefined" ||
        myretailers.next_page_url === null
      );
      this.total = myretailers.total || 0;
      this.totalPages = myretailers.total
        ? Math.ceil(myretailers.total / myretailers.per_page)
        : 0;

      // myretailers.total/myretailers.per_page

   

      //  console.log("myretailers.data",myretailers.data)

      // !myretailers.data === undefined && myretailers.data?.forEach((item, index)=>{
      myretailers.data?.forEach((item, index) => {
        myresult.push([
          item["name"],
          item["email"],
          item["phone"],
          item["business_name"],
          item["address"],
          item["country"],
          item["balance"],
          item["status"] === 0 ? "Inative" : "Active",
          item["user_id"],
        ]);
        myretailers_by_id[item["user_id"]] = { ...item, id: item["user_id"] };
      });


      this.retailers = myresult;
      this.retailers_by_id = myretailers_by_id;

      this.isLoading = false;
    },
    async reload_list() {
      this.currentPage = 1;
      this.isLoading = true;
      this.hasError = false;

      try {
        await this.$store.dispatch("admins/action_retailer_list", {
          country: this.country,
          page: this.currentPage,
          perPage: this.perPage,
          toast: this.$toast,
          router: this.$router,
        });

        this.filter_retailers();
      } catch (error) {
        this.hasError = true;
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.dmcenter {
  display: inline-block;
  position: relative;
  /*left: 50%;*/
  /*transform: translateX(-50%);*/
}
</style>
